import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, 
  Search, 
  Plus, 
  Edit2, 
  Trash2,
  Clock,
  Users,
  ChefHat,
  Scale,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';

const RECIPE_CATEGORIES = ['glazed', 'frosted', 'filled', 'specialty', 'fritter', 'seasonal'];

const StaffRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [scaleDialogOpen, setScaleDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [recipesData, ingredientsData] = await Promise.all([
        staffService.getRecipes(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
        staffService.getInventory()
      ]);
      setRecipes(recipesData);
      setIngredients(ingredientsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateRecipe = async (data) => {
    try {
      await staffService.createRecipe(data);
      toast.success('Recipe created');
      loadData();
      setCreateDialogOpen(false);
    } catch (error) {
      toast.error('Failed to create recipe');
    }
  };

  const handleDeleteRecipe = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    try {
      await staffService.deleteRecipe(id);
      toast.success('Recipe deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  const filteredRecipes = recipes.filter(recipe =>
    recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-recipes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recipes</h1>
          <p className="text-sm text-gray-600 mt-1">{recipes.length} recipes</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-2" />
              Add Recipe
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Recipe</DialogTitle>
              <DialogDescription className="sr-only">Create New Recipe dialog. Use the form below to continue.</DialogDescription>
            </DialogHeader>
            <RecipeForm
              ingredients={ingredients}
              onSubmit={handleCreateRecipe}
              onCancel={() => setCreateDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search recipes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {RECIPE_CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Recipes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredRecipes.map((recipe) => (
          <Card key={recipe.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <BookOpen className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{recipe.name}</h3>
                    <Badge variant="outline" className="capitalize text-xs mt-1">
                      {recipe.category}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="h-4 w-4" />
                  <span>Yields {recipe.yield_quantity} {recipe.yield_unit}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{recipe.total_time} min</span>
                </div>
              </div>

              <div className="text-sm text-gray-500 mb-4">
                {recipe.ingredients?.length || 0} ingredients
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setSelectedRecipe(recipe);
                    setViewDialogOpen(true);
                  }}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setSelectedRecipe(recipe);
                    setScaleDialogOpen(true);
                  }}
                >
                  <Scale className="h-4 w-4 mr-1" />
                  Scale
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteRecipe(recipe.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredRecipes.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No recipes found</p>
        </div>
      )}

      {/* View Recipe Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRecipe?.name}</DialogTitle>
            <DialogDescription className="sr-only"> dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          {selectedRecipe && <RecipeView recipe={selectedRecipe} />}
        </DialogContent>
      </Dialog>

      {/* Scale Recipe Dialog */}
      <Dialog open={scaleDialogOpen} onOpenChange={setScaleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Scale Recipe</DialogTitle>
            <DialogDescription className="sr-only">Scale Recipe dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          {selectedRecipe && (
            <ScaleForm
              recipe={selectedRecipe}
              onClose={() => setScaleDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Recipe View Component
const RecipeView = ({ recipe }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
        <div className="text-center">
          <p className="text-2xl font-bold text-orange-600">{recipe.yield_quantity}</p>
          <p className="text-sm text-gray-600">{recipe.yield_unit}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600">{recipe.prep_time_minutes}</p>
          <p className="text-sm text-gray-600">prep mins</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">{recipe.cook_time_minutes}</p>
          <p className="text-sm text-gray-600">cook mins</p>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Ingredients</h4>
        <ul className="space-y-2">
          {recipe.ingredients?.map((ing) => (
            <li key={ing.ingredient_name} className="flex items-center gap-2 text-gray-700">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              <span className="font-medium">{ing.quantity} {ing.unit}</span>
              <span>{ing.ingredient_name}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Instructions</h4>
        <ol className="space-y-3">
          {recipe.instructions?.map((step, i) => (
            <li key={`step-${i}`} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-sm font-medium">
                {i + 1}
              </span>
              <span className="text-gray-700">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {recipe.notes && (
        <div className="p-4 bg-amber-50 rounded-lg">
          <h4 className="font-semibold text-amber-800 mb-1">Notes</h4>
          <p className="text-amber-700 text-sm">{recipe.notes}</p>
        </div>
      )}
    </div>
  );
};

// Scale Form Component
const ScaleForm = ({ recipe, onClose }) => {
  const [targetQuantity, setTargetQuantity] = useState(recipe.yield_quantity);
  const [scaledRecipe, setScaledRecipe] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleScale = async () => {
    setLoading(true);
    try {
      const result = await staffService.scaleRecipe(recipe.id, targetQuantity);
      setScaledRecipe(result);
    } catch (error) {
      toast.error('Failed to scale recipe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 rounded-lg">
        <p className="font-medium">{recipe.name}</p>
        <p className="text-sm text-gray-600">Original yield: {recipe.yield_quantity} {recipe.yield_unit}</p>
      </div>

      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <Label>Target Quantity</Label>
          <Input
            type="number"
            value={targetQuantity}
            onChange={(e) => setTargetQuantity(parseInt(e.target.value))}
            min={1}
          />
        </div>
        <Button onClick={handleScale} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
          <Scale className="h-4 w-4 mr-2" />
          Scale
        </Button>
      </div>

      {scaledRecipe && (
        <div className="mt-4 p-4 border rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold">Scaled Ingredients</h4>
            <Badge className="bg-orange-100 text-orange-700">
              {scaledRecipe.scale_factor}x
            </Badge>
          </div>
          <ul className="space-y-2">
            {scaledRecipe.scaled_ingredients.map((ing) => (
              <li key={ing.ingredient_name} className="flex justify-between text-sm">
                <span>{ing.ingredient_name}</span>
                <span className="font-medium">
                  {ing.scaled_quantity} {ing.unit}
                  <span className="text-gray-400 ml-2">(was {ing.original_quantity})</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Recipe Form Component
const RecipeForm = ({ ingredients, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'glazed',
    yield_quantity: 12,
    yield_unit: 'donuts',
    prep_time_minutes: 30,
    cook_time_minutes: 15,
    ingredients: [],
    instructions: [''],
    notes: ''
  });

  const [newIngredient, setNewIngredient] = useState({
    ingredient_id: '',
    ingredient_name: '',
    quantity: '',
    unit: ''
  });

  const addIngredient = () => {
    if (!newIngredient.ingredient_id || !newIngredient.quantity) return;
    
    const ing = ingredients.find(i => i.id === newIngredient.ingredient_id);
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, {
        ingredient_id: newIngredient.ingredient_id,
        ingredient_name: ing.name,
        quantity: parseFloat(newIngredient.quantity),
        unit: newIngredient.unit || ing.unit
      }]
    });
    setNewIngredient({ ingredient_id: '', ingredient_name: '', quantity: '', unit: '' });
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData({ ...formData, instructions: newInstructions });
  };

  const addInstruction = () => {
    setFormData({ ...formData, instructions: [...formData.instructions, ''] });
  };

  const removeInstruction = (index) => {
    setFormData({
      ...formData,
      instructions: formData.instructions.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      instructions: formData.instructions.filter(s => s.trim())
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Recipe Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Classic Glazed Donut"
            required
          />
        </div>
        <div>
          <Label>Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECIPE_CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <Label>Yield</Label>
            <Input
              type="number"
              value={formData.yield_quantity}
              onChange={(e) => setFormData({ ...formData, yield_quantity: parseInt(e.target.value) })}
            />
          </div>
          <div className="flex-1">
            <Label>Unit</Label>
            <Input
              value={formData.yield_unit}
              onChange={(e) => setFormData({ ...formData, yield_unit: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label>Prep Time (mins)</Label>
          <Input
            type="number"
            value={formData.prep_time_minutes}
            onChange={(e) => setFormData({ ...formData, prep_time_minutes: parseInt(e.target.value) })}
          />
        </div>
        <div>
          <Label>Cook Time (mins)</Label>
          <Input
            type="number"
            value={formData.cook_time_minutes}
            onChange={(e) => setFormData({ ...formData, cook_time_minutes: parseInt(e.target.value) })}
          />
        </div>
      </div>

      {/* Ingredients */}
      <div>
        <Label className="mb-2 block">Ingredients</Label>
        <div className="space-y-2 mb-3">
          {formData.ingredients.map((ing, i) => (
            <div key={ing.ingredient_id || `ing-${i}`} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
              <span className="flex-1">{ing.quantity} {ing.unit} {ing.ingredient_name}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeIngredient(i)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Select
            value={newIngredient.ingredient_id}
            onValueChange={(v) => {
              const ing = ingredients.find(i => i.id === v);
              setNewIngredient({ ...newIngredient, ingredient_id: v, unit: ing?.unit || '' });
            }}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select ingredient" />
            </SelectTrigger>
            <SelectContent>
              {ingredients.map(ing => (
                <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            step="0.01"
            placeholder="Qty"
            className="w-20"
            value={newIngredient.quantity}
            onChange={(e) => setNewIngredient({ ...newIngredient, quantity: e.target.value })}
          />
          <Input
            placeholder="Unit"
            className="w-20"
            value={newIngredient.unit}
            onChange={(e) => setNewIngredient({ ...newIngredient, unit: e.target.value })}
          />
          <Button type="button" onClick={addIngredient} variant="outline">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <div>
        <Label className="mb-2 block">Instructions</Label>
        <div className="space-y-2">
          {formData.instructions.map((step, i) => (
            <div key={`instr-${i}`} className="flex gap-2">
              <span className="flex-shrink-0 w-6 h-9 flex items-center justify-center text-sm font-medium text-gray-500">
                {i + 1}.
              </span>
              <Input
                value={step}
                onChange={(e) => updateInstruction(i, e.target.value)}
                placeholder="Add instruction step..."
              />
              {formData.instructions.length > 1 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeInstruction(i)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addInstruction} className="mt-2">
          <Plus className="h-4 w-4 mr-1" /> Add Step
        </Button>
      </div>

      {/* Notes */}
      <div>
        <Label>Notes (Optional)</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="bg-orange-500 hover:bg-orange-600">Create Recipe</Button>
      </div>
    </form>
  );
};

export default StaffRecipes;
